import cx from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Container, Icon, Loader } from 'semantic-ui-react';

import { CmsPageLayout } from '../cms/CmsPage';
import { bem } from '../core/design/bem';
import { useMe } from '../profile/hooks';
import './GroupVisits.scss';
import GroupDetailsModal from './components/GroupDetailsModal';
import GroupFormModal from './components/GroupFormModal';
import GroupQuotaHeader from './components/GroupQuotaHeader';
import GroupsTable from './components/GroupsTable';
import { useGroups, useGroupsBySlot } from './hooks/useGroups';
import { GROUPS_COLLECTION, GroupWithInvited, INVITED_COLLECTION } from './types';
import { formatSlotRange } from './utils';

const css = bem('GroupVisitsPage');
const translationPrefix = 'group-visits';

type GroupVisitsPageProps = {
  pageId: string;
  formSlug: string;
  groupsCollection?: string;
  invitedCollection?: string;
  header?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  design?: Record<string, unknown>;
  pageHeader?: Record<string, unknown>;
  pageFooter?: Record<string, unknown>;
};

function formatSlot(slot: string, t: (key: string) => string): string {
  if (!slot) return t(`${translationPrefix}.slot.no-slot`);
  return formatSlotRange(slot);
}

const GroupVisitsPage = ({
  pageId,
  formSlug,
  groupsCollection = GROUPS_COLLECTION,
  invitedCollection = INVITED_COLLECTION,
  header,
  metadata,
  design,
  pageHeader,
  pageFooter,
}: GroupVisitsPageProps): JSX.Element => {
  const { t } = useTranslation();
  const me = useMe();
  const { groups, isLoading, refetch } = useGroups(groupsCollection, invitedCollection);
  const groupsBySlot = useGroupsBySlot(groups);

  // `null` = modale fermée, `undefined` = création, `Group` = édition
  const [editing, setEditing] = useState<GroupWithInvited | null | undefined>(null);
  const isModalOpen = editing !== null;
  const [details, setDetails] = useState<GroupWithInvited | null>(null);

  // `groupQuota` peut arriver en nombre ou en chaîne numérique ("5")
  const rawQuota = me?.groupQuota;
  const quota = rawQuota == null || rawQuota === '' ? NaN : Number(rawQuota);
  const hasQuota = !Number.isNaN(quota);
  const quotaReached = hasQuota && groups.length >= quota;

  const handleClose = () => {
    setEditing(null);
    refetch();
  };

  return (
    <CmsPageLayout
      className={cx('page', 'page--group-visits', pageId)}
      header={header}
      metadata={metadata}
      designOverride={design}
      pageHeader={pageHeader}
      pageFooter={pageFooter}
    >
      <Container className={css().toString()}>
        <div className={css('toolbar').toString()}>
          {hasQuota && <GroupQuotaHeader groupCount={groups.length} quota={quota} />}
          <Button
            primary
            className={css('create').toString()}
            disabled={quotaReached}
            onClick={() => setEditing(undefined)}
          >
            <Icon name="plus" />
            {t(`${translationPrefix}.create-group`)}
          </Button>
        </div>

        {isLoading && <Loader active inline="centered" />}

        {!isLoading && groups.length === 0 && (
          <div className={css('empty').toString()}>{t(`${translationPrefix}.empty`)}</div>
        )}

        {groupsBySlot.map(({ slot, groups: slotGroups }) => (
          <section key={slot || 'no-slot'} className={css('slot').toString()}>
            <div className={css('slot-header').toString()}>
              <Icon name="calendar alternate outline" />
              <span className={css('slot-title').toString()}>{formatSlot(slot, t)}</span>
              <span className={css('slot-count').toString()}>
                {t(`${translationPrefix}.slot.group-count`, { count: slotGroups.length })}
              </span>
            </div>
            <div className={css('table-wrapper').toString()}>
              <GroupsTable groups={slotGroups} onDetails={setDetails} />
            </div>
          </section>
        ))}
      </Container>

      {isModalOpen && me?._id && (
        <GroupFormModal
          formSlug={formSlug}
          userId={me._id}
          group={editing || undefined}
          onClose={handleClose}
        />
      )}

      {details && (
        <GroupDetailsModal
          group={details}
          onClose={() => setDetails(null)}
          onEdit={(group) => {
            setDetails(null);
            setEditing(group);
          }}
        />
      )}
    </CmsPageLayout>
  );
};

GroupVisitsPage.defaultProps = {
  groupsCollection: GROUPS_COLLECTION,
  invitedCollection: INVITED_COLLECTION,
  header: undefined,
  metadata: undefined,
  design: {},
  pageHeader: undefined,
  pageFooter: undefined,
};

export default GroupVisitsPage;
